import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

const G = '#004D35';
const MINT = '#a8f0c6';
const DARK = '#0a1a0f';
const BG = '#f5f3ef';
const BORDER = '#e8e5e0';
const AMBER = '#b45309';
const AMBER_BG = '#fffbeb';
const RED = '#c0392b';
const RED_BG = '#fdf2f2';

function LogoFull() {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      <div style={{ width: 32, height: 32, background: G, borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <svg width="18" height="18" viewBox="0 0 40 40" fill="none">
          <path d="M10 11 L20 28 L30 11" stroke="#fff" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round" />
          <circle cx="20" cy="28" r="2.5" fill="#a8f0c6" />
        </svg>
      </div>
      <span style={{ fontSize: 17, fontWeight: 700, color: G, fontFamily: 'Georgia, serif' }}>Viysr</span>
    </div>
  );
}

function Toast({ msg }) {
  if (!msg) return null;
  return (
    <div style={{ position: 'fixed', bottom: 24, left: '50%', transform: 'translateX(-50%)', background: DARK, color: '#fff', padding: '12px 28px', borderRadius: 999, fontSize: 14, fontFamily: 'sans-serif', zIndex: 600, boxShadow: '0 8px 32px rgba(0,0,0,0.2)', whiteSpace: 'nowrap' }}>
      {msg}
    </div>
  );
}

const STATUS_STYLES = {
  pending:  { label: 'Pending',  color: AMBER,  bg: AMBER_BG,  border: '#fcd34d' },
  approved: { label: 'Approved', color: G,       bg: '#f0fdf6', border: '#a8f0c6' },
  rejected: { label: 'Rejected', color: '#555',  bg: BG,        border: BORDER    },
};

function StatusBadge({ status }) {
  const s = STATUS_STYLES[status] || STATUS_STYLES.pending;
  return (
    <span style={{ fontSize: 11, fontWeight: 700, color: s.color, background: s.bg, border: `1px solid ${s.border}`, padding: '3px 10px', borderRadius: 999 }}>
      {s.label}
    </span>
  );
}

export default function AdvisorTestimonialsPage() {
  const { getAuthHeader, signOut } = useAuth();
  const navigate = useNavigate();

  const [testimonials, setTestimonials] = useState([]);
  const [requestLink, setRequestLink] = useState('');
  const [existingToken, setExistingToken] = useState(null);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState('');
  const [linkLoading, setLinkLoading] = useState(false);
  const [regenLoading, setRegenLoading] = useState(false);
  const [copied, setCopied] = useState(false);

  const showToast = (msg) => { setToast(msg); setTimeout(() => setToast(''), 3000); };

  const loadData = useCallback(async () => {
    try {
      const headers = await getAuthHeader();
      const res = await fetch('/api/testimonials/advisor/mine', { headers });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      setTestimonials(data.testimonials || []);
      if (data.token) {
        setExistingToken(data.token);
        const host = window.location.host;
        const protocol = window.location.protocol;
        setRequestLink(`${protocol}//${host}/testimonial/${data.token}`);
      }
    } catch (err) {
      console.error('[Testimonials] load error:', err);
    } finally {
      setLoading(false);
    }
  }, [getAuthHeader]);

  useEffect(() => { loadData(); }, [loadData]);

  const handleGetLink = async () => {
    setLinkLoading(true);
    try {
      const headers = await getAuthHeader();
      const res = await fetch('/api/testimonials/advisor/request-link', {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      setRequestLink(data.link);
      setExistingToken(data.token);
      showToast('Link generated!');
    } catch (err) {
      showToast('Failed to generate link');
    } finally {
      setLinkLoading(false);
    }
  };

  const handleCopyLink = () => {
    navigator.clipboard.writeText(requestLink);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
    showToast('Link copied to clipboard!');
  };

  const handleRegenLink = async () => {
    if (!window.confirm('This will invalidate the current link. Anyone with the old link won\'t be able to submit. Continue?')) return;
    setRegenLoading(true);
    try {
      const headers = await getAuthHeader();
      const res = await fetch('/api/testimonials/advisor/regenerate-link', {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      setRequestLink(data.link);
      setExistingToken(data.token);
      setCopied(false);
      showToast('New link generated — old link is now invalid');
    } catch (err) {
      showToast('Failed to regenerate link');
    } finally {
      setRegenLoading(false);
    }
  };

  const handleAction = async (id, action) => {
    try {
      const headers = await getAuthHeader();
      let res;
      if (action === 'delete') {
        res = await fetch(`/api/testimonials/advisor/${id}`, { method: 'DELETE', headers });
      } else {
        res = await fetch(`/api/testimonials/advisor/${id}/${action}`, { method: 'PATCH', headers });
      }
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      showToast(action === 'approve' ? 'Approved!' : action === 'reject' ? 'Rejected' : 'Deleted');
      await loadData();
    } catch (err) {
      showToast('Action failed. Please try again.');
    }
  };

  const handleSignOut = async () => { await signOut(); navigate('/'); };

  const pending  = testimonials.filter(t => t.status === 'pending');
  const approved = testimonials.filter(t => t.status === 'approved');
  const rejected = testimonials.filter(t => t.status === 'rejected');

  return (
    <div style={{ minHeight: '100vh', background: BG, fontFamily: 'sans-serif' }}>
      <Toast msg={toast} />

      <nav style={{ background: '#fff', padding: '0 40px', borderBottom: `1px solid ${BORDER}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between', height: 60, position: 'sticky', top: 0, zIndex: 50 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <Link to="/advisor/dashboard" style={{ textDecoration: 'none' }}><LogoFull /></Link>
          <span style={{ fontSize: 13, color: '#ccc' }}>→</span>
          <Link to="/advisor/dashboard" style={{ fontSize: 13, color: '#aaa', textDecoration: 'none' }}>Dashboard</Link>
          <span style={{ fontSize: 13, color: '#ccc' }}>→</span>
          <span style={{ fontSize: 13, color: '#444', fontWeight: 600 }}>Testimonials</span>
        </div>
        <button onClick={handleSignOut} style={{ background: 'none', border: 'none', fontSize: 13, color: '#bbb', cursor: 'pointer' }}>Sign out</button>
      </nav>

      <div style={{ maxWidth: 700, margin: '40px auto', padding: '0 24px' }}>

        {/* ── REQUEST LINK ── */}
        <div style={{ background: '#fff', border: `1.5px solid ${BORDER}`, borderRadius: 16, padding: '28px 32px', marginBottom: 24 }}>
          <h2 style={{ fontSize: 17, fontWeight: 700, color: DARK, fontFamily: 'Georgia, serif', margin: '0 0 4px' }}>Testimonial request link</h2>
          <p style={{ fontSize: 13, color: '#aaa', margin: '0 0 20px' }}>Share this link with clients — they can submit a testimonial without an account. The link is reusable.</p>

          {requestLink ? (
            <div>
              <div style={{ display: 'flex', gap: 10, alignItems: 'stretch', marginBottom: 10 }}>
                <input
                  readOnly
                  value={requestLink}
                  style={{ flex: 1, padding: '10px 14px', border: `1.5px solid ${BORDER}`, borderRadius: 10, fontSize: 13, color: '#555', background: BG, outline: 'none', fontFamily: 'monospace' }}
                />
                <button
                  onClick={handleCopyLink}
                  style={{ padding: '10px 20px', background: copied ? MINT : '#fff', border: `1.5px solid ${copied ? G : BORDER}`, color: copied ? G : '#555', borderRadius: 10, fontSize: 13, fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap', transition: 'all 0.2s' }}
                >
                  {copied ? '✓ Copied' : 'Copy link'}
                </button>
              </div>
              <button
                onClick={handleRegenLink}
                disabled={regenLoading}
                style={{ background: 'none', border: 'none', padding: 0, fontSize: 12, color: '#bbb', cursor: regenLoading ? 'not-allowed' : 'pointer', textDecoration: 'underline' }}
              >
                {regenLoading ? 'Regenerating…' : 'Regenerate link (invalidates current)'}
              </button>
            </div>
          ) : (
            <button
              onClick={handleGetLink}
              disabled={linkLoading}
              style={{ background: G, color: '#fff', border: 'none', padding: '12px 24px', borderRadius: 999, fontSize: 14, fontWeight: 700, cursor: linkLoading ? 'not-allowed' : 'pointer', opacity: linkLoading ? 0.7 : 1 }}
            >
              {linkLoading ? 'Generating…' : 'Generate testimonial link'}
            </button>
          )}
        </div>

        {/* ── PENDING ── */}
        {pending.length > 0 && (
          <div style={{ background: '#fff', border: `1.5px solid ${BORDER}`, borderRadius: 16, padding: '28px 32px', marginBottom: 20 }}>
            <h2 style={{ fontSize: 17, fontWeight: 700, color: DARK, fontFamily: 'Georgia, serif', margin: '0 0 16px' }}>
              Awaiting review <span style={{ fontSize: 13, fontWeight: 400, color: '#aaa', fontFamily: 'sans-serif' }}>({pending.length})</span>
            </h2>
            {pending.map(t => (
              <TestimonialCard key={t.id} t={t} onAction={handleAction} showActions />
            ))}
          </div>
        )}

        {/* ── APPROVED ── */}
        {approved.length > 0 && (
          <div style={{ background: '#fff', border: `1.5px solid ${BORDER}`, borderRadius: 16, padding: '28px 32px', marginBottom: 20 }}>
            <h2 style={{ fontSize: 17, fontWeight: 700, color: DARK, fontFamily: 'Georgia, serif', margin: '0 0 16px' }}>
              Approved <span style={{ fontSize: 13, fontWeight: 400, color: '#aaa', fontFamily: 'sans-serif' }}>({approved.length})</span>
            </h2>
            {approved.map(t => (
              <TestimonialCard key={t.id} t={t} onAction={handleAction} showDelete />
            ))}
          </div>
        )}

        {/* ── REJECTED ── */}
        {rejected.length > 0 && (
          <div style={{ background: '#fff', border: `1.5px solid ${BORDER}`, borderRadius: 16, padding: '28px 32px', marginBottom: 20 }}>
            <h2 style={{ fontSize: 17, fontWeight: 700, color: DARK, fontFamily: 'Georgia, serif', margin: '0 0 16px' }}>
              Rejected <span style={{ fontSize: 13, fontWeight: 400, color: '#aaa', fontFamily: 'sans-serif' }}>({rejected.length})</span>
            </h2>
            {rejected.map(t => (
              <TestimonialCard key={t.id} t={t} onAction={handleAction} showDelete />
            ))}
          </div>
        )}

        {!loading && testimonials.length === 0 && (
          <div style={{ background: '#fff', border: `1.5px solid ${BORDER}`, borderRadius: 16, padding: '40px 32px', textAlign: 'center' }}>
            <div style={{ fontSize: 32, marginBottom: 12 }}>💬</div>
            <div style={{ fontSize: 16, fontWeight: 700, color: DARK, fontFamily: 'Georgia, serif', marginBottom: 8 }}>No testimonials yet</div>
            <div style={{ fontSize: 14, color: '#888' }}>Generate a link above and share it with clients to start collecting testimonials.</div>
          </div>
        )}
      </div>
    </div>
  );
}

function TestimonialCard({ t, onAction, showActions, showDelete }) {
  const DARK = '#0a1a0f';
  const BORDER = '#e8e5e0';
  const G = '#004D35';
  const RED = '#c0392b';
  const RED_BG = '#fdf2f2';

  const date = new Date(t.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

  return (
    <div style={{ borderBottom: `1px solid ${BORDER}`, paddingBottom: 20, marginBottom: 20 }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, marginBottom: 8 }}>
        <div>
          <div style={{ fontSize: 14, fontWeight: 700, color: DARK }}>{t.author_name}</div>
          {t.company_name && <div style={{ fontSize: 13, color: '#888' }}>{t.company_name}</div>}
          <div style={{ fontSize: 12, color: '#bbb', marginTop: 2 }}>{date}</div>
        </div>
        <StatusBadge status={t.status} />
      </div>

      <p style={{ fontSize: 14, color: '#555', lineHeight: 1.7, margin: '0 0 12px', fontStyle: 'italic' }}>
        "{t.text}"
      </p>

      {showActions && (
        <div style={{ display: 'flex', gap: 8 }}>
          <button
            onClick={() => onAction(t.id, 'approve')}
            style={{ background: G, color: '#fff', border: 'none', padding: '7px 16px', borderRadius: 999, fontSize: 12, fontWeight: 700, cursor: 'pointer' }}
          >
            ✓ Approve
          </button>
          <button
            onClick={() => onAction(t.id, 'reject')}
            style={{ background: '#fff', color: '#666', border: `1.5px solid ${BORDER}`, padding: '7px 16px', borderRadius: 999, fontSize: 12, fontWeight: 600, cursor: 'pointer' }}
          >
            Reject
          </button>
          <button
            onClick={() => onAction(t.id, 'delete')}
            style={{ background: RED_BG, color: RED, border: `1.5px solid #f5c6c2`, padding: '7px 16px', borderRadius: 999, fontSize: 12, fontWeight: 600, cursor: 'pointer' }}
          >
            Delete
          </button>
        </div>
      )}
      {showDelete && (
        <button
          onClick={() => onAction(t.id, 'delete')}
          style={{ background: '#fff', color: '#aaa', border: `1.5px solid ${BORDER}`, padding: '7px 16px', borderRadius: 999, fontSize: 12, fontWeight: 600, cursor: 'pointer' }}
        >
          Delete
        </button>
      )}
    </div>
  );
}

